import React from 'react';
import { FieldArray, useFormikContext } from 'formik';

import Button from '~/components/core/Atomic/Buttons/Button';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import { useFormikFieldError } from '~/components/core/Formik/formikErrorUtils';
import Heading from '~/components/core/TextComponents/Heading';
import Text from '~/components/core/TextComponents/Text';
import PlusIcon from '~/components/icons/PlusIcon';
import TrashIcon from '~/components/icons/TrashIcon';
import TextFieldFormikUntyped from '~/components/TextFieldFormik';

import { TEXTS } from '../texts';
import type { SlaRecipientOption } from '../types';

import type { TemplatesLoadFailedChangeHandler } from './ActionsEditor';
import ActionsEditor from './ActionsEditor';
import type { SlaRuleFormValues } from './formValues';
import { EMPTY_THRESHOLD } from './formValues';

interface TextFieldFormikProps {
  id: string;
  label: string;
  type?: 'number';
  disabled?: boolean;
}
// Assertion over a still-untyped JS component, which the TS rule allows for gradual migration: it
// only narrows the props this file passes, so a wrong prop name or type is still a compile error.
const TextFieldFormik = TextFieldFormikUntyped as React.ComponentType<TextFieldFormikProps>;

interface DaysFieldProps {
  id: string;
  label: string;
  unit: string;
  disabled: boolean;
}

const DaysField: React.FC<DaysFieldProps> = ({ id, label, unit, disabled }) => (
  <div className="flex items-baseline gap-8">
    <TextFieldFormik id={id} label={label} type="number" disabled={disabled} />
    <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
      {unit}
    </Text>
  </div>
);

interface ThresholdsEditorProps {
  recipientOptions: SlaRecipientOption[];
  organizationId: number;
  isClaimLevel: boolean;
  onTemplatesLoadFailedChange: TemplatesLoadFailedChangeHandler;
}

const ThresholdsEditor: React.FC<ThresholdsEditorProps> = ({
  recipientOptions,
  organizationId,
  isClaimLevel,
  onTemplatesLoadFailedChange,
}) => {
  const { values, isSubmitting } = useFormikContext<SlaRuleFormValues>();
  // The distinct-schedules rule reports on the list itself. Formik keeps that message only while no
  // field inside the list has an error of its own, which is when `thresholds` holds a string.
  const { error: listError } = useFormikFieldError('thresholds');
  // The repeat gap follows the business-days toggle as well as the SLA days.
  const unit = TEXTS.DAYS_UNIT(values.is_business_days);

  return (
    <div className="mb-20 flex flex-col gap-12">
      <Heading variant={Heading.TYPES.H3}>{TEXTS.THRESHOLDS_HEADING}</Heading>
      <FieldArray
        name="thresholds"
        render={({ push, remove }) => (
          <div className="flex flex-col gap-16">
            {values.thresholds.map((_, index) => (
              <div
                key={index}
                role="group"
                aria-label={TEXTS.THRESHOLD_TITLE(index + 1)}
                className="flex flex-col gap-12 rounded-md border border-solid border-slate-300 p-12"
              >
                <div className="flex items-center justify-between">
                  <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
                    {TEXTS.THRESHOLD_TITLE(index + 1)}
                  </Text>
                  <IconButton
                    size="small"
                    aria-label={TEXTS.REMOVE_THRESHOLD_ARIA_LABEL(index + 1)}
                    // The backend requires at least one threshold.
                    disabled={values.thresholds.length === 1 || isSubmitting}
                    onClick={() => remove(index)}
                  >
                    <TrashIcon />
                  </IconButton>
                </div>
                <div className="flex gap-24">
                  <DaysField
                    id={`thresholds.${index}.sla_days`}
                    label={TEXTS.SLA_DAYS_LABEL}
                    unit={unit}
                    disabled={isSubmitting}
                  />
                  <DaysField
                    id={`thresholds.${index}.repeat_every_days`}
                    label={TEXTS.REPEAT_EVERY_LABEL}
                    unit={unit}
                    disabled={isSubmitting}
                  />
                </div>
                <ActionsEditor
                  thresholdIndex={index}
                  recipientOptions={recipientOptions}
                  organizationId={organizationId}
                  isClaimLevel={isClaimLevel}
                  onTemplatesLoadFailedChange={onTemplatesLoadFailedChange}
                />
              </div>
            ))}
            {typeof listError === 'string' ? (
              <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.ERROR}>
                {listError}
              </Text>
            ) : null}
            <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
              {TEXTS.THRESHOLD_REMOVAL_NOTE}
            </Text>
            <div>
              <Button
                color="primary"
                onClick={() => push(EMPTY_THRESHOLD)}
                disabled={isSubmitting}
                startIcon={<PlusIcon className="text-teal-700" size={14} iconColor="currentColor" />}
              >
                {TEXTS.ADD_THRESHOLD}
              </Button>
            </div>
          </div>
        )}
      />
    </div>
  );
};

export default ThresholdsEditor;
